import PropTypes from "prop-types";
import * as R from "ramda";
import React, { useState } from "react";
import { Editor } from "@tinymce/tinymce-react";
import { openFilePicker } from "utils/General/sagas";
import { maxLengthPlugin } from "ui-kit/Form/View/AdvancedWYSIWYGWithoutSagas";
import { captureException } from "utils/errors";
import TextAreaEditor from "components/Global/StandAloneEditors/TextArea";
import styles from "./styles.scss";

const MODULES_TINY_MCE =
  "table media link code lists image codesample wordcount";
const FORMATS_TINY_MCE =
  "bold italic underline | superscript subscript link | bullist numlist outdent indent | image | removeformat | code | codesample";
const placeholder = "Content...";
const width = "100%";
const TINY_MCE_EXTERNAL_PLUGINS = {
  eqneditor: "https://static.sessionboard.com/js/eqneditor.js"
};

const handleImageUpload = async (insertUploadedImage, notifyError) => {
  const showError = (message = "Error uploading image") =>
    notifyError({ message });
  const uploadedImages = await openFilePicker({
    accept: ["image/*"],
    onFileUploadFailed: () => showError()
  });
  if (!uploadedImages.length) {
    showError();
    return;
  }
  try {
    const [{ url, filename }] = uploadedImages;
    insertUploadedImage(url, { title: filename });
  } catch {
    showError("Error inserting image");
  }
};

const WysiwygPortal = props => {
  const [useFallbackEditor, setUseFallbackEditor] = useState(false);

  const {
    ["aria-label"]: ariaLabel = "Rich text editor",
    column: { disabled, type },
    onBlur,
    onChange,
    settings = {},
    value: iniValue,
    value: { value } = {},
    modules = MODULES_TINY_MCE,
    formats = FORMATS_TINY_MCE,
    maxLength,
    useCustomCharCount = false
  } = props;

  const [inputValue, setValue] = useState(value ?? "");
  const handleOnChange = value => {
    setValue(value);
    onChange({ type, value });
  };
  const [charCounts, setCharCounts] = useState(iniValue?.length || 0);
  const isCharCountsEnabled = !R.isNil(maxLength) && useCustomCharCount;
  // handle: fallback editor
  const [fallbackInputValue, setFallbackInputValue] = useState(iniValue);
  const handleOnFallbackChange = value => {
    setFallbackInputValue(value);
    onChange({ type, value: value?.value ? value.value : null });
  };

  if (useFallbackEditor) {
    return (
      <TextAreaEditor
        {...{
          ...props,
          value: fallbackInputValue,
          continuous: true,
          onChange: val => handleOnFallbackChange(val),
          maxLength,
          style: {
            width: "100%"
          },
          minRows: 10
        }}
      />
    );
  }

  return (
    <div className={styles.wysiwygContainer}>
      <Editor
        tabIndex={0}
        aria-label={ariaLabel}
        // eslint-disable-next-line no-underscore-dangle
        apiKey={window.__TINY_MCE_API_KEY__}
        onScriptsLoadError={err => {
          captureException(err, {
            extra: {
              message:
                "[TinyMCE onScriptsLoadError] - Wysiwyg Standalone Editor"
            }
          });
          setUseFallbackEditor(true);
        }}
        onEditorChange={val => handleOnChange(val)}
        onBlur={onBlur ? () => onBlur(inputValue) : undefined}
        value={inputValue}
        disabled={disabled}
        init={{
          width,
          placeholder,
          plugins: modules,
          toolbar: formats,
          relative_urls: false,
          remove_script_host: false,
          file_picker_callback: callback =>
            handleImageUpload(callback, () => {}),
          setup: editor => {
            if (maxLength) {
              maxLengthPlugin(
                editor,
                maxLength,
                useCustomCharCount,
                useCustomCharCount ? setCharCounts : null
              );
            }
          },
          default_link_target: "_blank",
          paste_as_text: true,
          menubar: false,
          branding: false,
          image_caption: true,
          image_title: true,
          urlconverter_callback: url => url,
          external_plugins: {
            eqneditor: TINY_MCE_EXTERNAL_PLUGINS.eqneditor
          },
          ...settings
        }}
      />
      {isCharCountsEnabled ? (
        <div className={styles.charcounts}>
          {charCounts}/{maxLength} Character
          {charCounts !== 1 ? "s" : ""}
        </div>
      ) : null}
    </div>
  );
};

WysiwygPortal.propTypes = {
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  value: PropTypes.object
};

export default WysiwygPortal;
